import {
  BooleanField,
  Datagrid,
  FilterPayload,
  Filter,
  FunctionField,
  Identifier,
  Labeled,
  List,
  RaRecord,
  ReferenceField,
  TextInput,
} from "react-admin";
import CargoContext from "../../contexts/CargoContext";
import React, { useCallback, useState } from "react";
import { useTheme } from "@mui/material/styles";
import { Box, Grid, Paper, Stack, Typography } from "@mui/material";
import { Cargo } from "../../models/Cargo";
import { TruncatedTextField, UserAvatarWithName } from "./common";
import { User } from "../../models/common";
import { SmallCargoCard } from "./small-cargo-card";

interface CargoFilterProps {
  [key: string]: any;
}

const CargoFilter: React.FC<CargoFilterProps> = (props) => (
  <Filter {...props}>
    <TextInput source="name" label="货物名搜索" alwaysOn autoComplete="name" />
  </Filter>
);

const formatDateTime = (value: string | null | undefined) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  const pad = (n: number) => String(n).padStart(2, "0");

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
    date.getSeconds()
  )}`;
};

const formatDepartureTime = (cargo: Cargo) => {
  if (!cargo.expect_departure_at) {
    return "-";
  }

  const date = new Date(cargo.expect_departure_at);
  const pad = (n: number) => String(n).padStart(2, "0");

  if (cargo.continuous) {
    return `每天 ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
      date.getSeconds()
    )}`;
  }

  return formatDateTime(cargo.expect_departure_at);
};

const formatCargoStatus = (status: number) => {
  switch (status) {
    case 1:
      return "上架中";
    case 2:
      return "主动下架";
    case 3:
      return "超时下架";
    default:
      return "-";
  }
};

const formatCargoPrice = (cargo: Cargo) => {
  if (cargo.single_price != null) {
    return `${cargo.single_price} 元/车`;
  }

  if (cargo.unit_price != null) {
    return `${cargo.unit_price} 元/吨`;
  }

  return "-";
};

const CargoUserReferenceField = ({
  source,
  label,
}: {
  source: "owner" | "poster";
  label: string;
}) => (
  <FunctionField
    label={label}
    source={source}
    render={(record: Cargo) => {
      const user = record[source];

      if (!user) {
        return "-";
      }

      if (typeof user === "object") {
        return <UserAvatarWithName record={user as User} />;
      }

      return (
        <ReferenceField
          reference="users"
          source={source}
          sortBy="name"
          link={false}
        >
          <FunctionField
            render={(record: User) =>
              record ? <UserAvatarWithName record={record} /> : "-"
            }
          />
        </ReferenceField>
      );
    }}
  />
);

const CargoUserInline = ({
  source,
  label,
}: {
  source: "owner" | "poster";
  label: string;
}) => (
  <Stack spacing={0.25}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <CargoUserReferenceField source={source} label={label} />
  </Stack>
);

const CargoDetail: React.FC = () => {
  return (
    <Paper elevation={3} style={{ padding: "20px" }}>
      <Grid container spacing={2}>
        <Grid item xs={3}>
          <Labeled>
            <FunctionField
              source="status"
              label="状态"
              render={(record: Cargo) => formatCargoStatus(record.status)}
            />
          </Labeled>
        </Grid>
        <Grid item xs={3}>
          <Labeled>
            <FunctionField
              source="continuous"
              label="货源类型"
              render={(record: Cargo) =>
                record.continuous ? "长期货源" : "单次货源"
              }
            />
          </Labeled>
        </Grid>
        <Grid item xs={3}>
          <Labeled label="是否退还押金">
            <BooleanField source="deposit_refund" />
          </Labeled>
        </Grid>
        <Grid item xs={3}>
          <Labeled label="计价方式">
            <FunctionField
              render={(record: Cargo) => formatCargoPrice(record)}
            />
          </Labeled>
        </Grid>
        <Grid item xs={3}>
          <Labeled label="无预付款">
            <BooleanField source="no_pre_billing" />
          </Labeled>
        </Grid>
        <Grid item xs={3}>
          <Labeled label="上架时间">
            <FunctionField
              render={(record: Cargo) => formatDateTime(record.publish_at)}
            />
          </Labeled>
        </Grid>
        <Grid item xs={3}>
          <Labeled label="货主">
            <CargoUserReferenceField source="owner" label="货主" />
          </Labeled>
        </Grid>
        <Grid item xs={3}>
          <Labeled label="发货人">
            <CargoUserReferenceField source="poster" label="发货人" />
          </Labeled>
        </Grid>
        <Grid item xs={3}>
          <Labeled label="货物描述">
            <TruncatedTextField source="description" />
          </Labeled>
        </Grid>
        <Grid item xs={6}>
          <Labeled label="发货地">
            <FunctionField
              render={(record: Cargo) =>
                `${record.from.province} ${record.from.city} ${record.from.district} ${record.from.address}`
              }
            />
          </Labeled>
        </Grid>
        <Grid item xs={6}>
          <Labeled label="收货地">
            <FunctionField
              render={(record: Cargo) =>
                `${record.to.province} ${record.to.city} ${record.to.district} ${record.to.address}`
              }
            />
          </Labeled>
        </Grid>
      </Grid>
    </Paper>
  );
};
interface CargoListProps {
  filter?: FilterPayload;
}

// 
export const CargoList: React.FC<CargoListProps> = ({ filter }) => {
  const { setSelectedCargo } = React.useContext(CargoContext);
  const [selectedRow, setSelectedRow] = useState<Identifier | null>(null);

  const handleRowClick = useCallback(
    (id: Identifier, resource: string, record: RaRecord) => {
      setSelectedCargo(record as Cargo);
      setSelectedRow(id);
      return false;
    },
    [setSelectedCargo, setSelectedRow]
  );

  const theme = useTheme();

  const rowSx = useCallback(
    (record: RaRecord) => ({
      backgroundColor:
        record.id === selectedRow
          ? theme.palette.action.selected
          : theme.palette.background.paper,
    }),
    [selectedRow, theme]
  );

  return (
    <List
      resource="validCargos"
      sort={{ field: "id", order: "DESC" }}
      filter={filter}
      filters={<CargoFilter />}
      perPage={10}
    >
      <Datagrid
        bulkActionButtons={false}
        rowClick={handleRowClick}
        expand={<CargoDetail />}
        rowSx={rowSx}
        size="small"
        sx={{
          "& .RaDatagrid-headerCell": {
            px: 1,
            py: 0.75,
            fontSize: 12,
            fontWeight: 700,
            whiteSpace: "nowrap",
          },
          "& .RaDatagrid-rowCell": {
            px: 1,
            py: 1,
            verticalAlign: "top",
          },
          "& .column-cargoInfo": { width: "44%" },
          "& .column-scheduleInfo": { width: "20%" },
          "& .column-priceInfo": { width: "16%" },
          "& .column-userInfo": { width: "20%" },
        }}
      >
        <FunctionField
          source="cargoInfo"
          label="货源信息"
          sortBy="name"
          render={(record: Cargo) => SmallCargoCard(record)}
        />
        <FunctionField
          source="scheduleInfo"
          label="时间"
          sortBy="publish_at"
          render={(record: Cargo) => (
            <Stack spacing={0.5} sx={{ minWidth: 130 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  上架
                </Typography>
                <Typography variant="body2">
                  {formatDateTime(record.publish_at)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  装车
                </Typography>
                <Typography variant="body2">
                  {formatDepartureTime(record)}
                </Typography>
              </Box>
            </Stack>
          )}
        />
        <FunctionField
          source="priceInfo"
          label="报价"
          render={(record: Cargo) => (
            <Stack spacing={0.5} sx={{ minWidth: 104 }}>
              <Typography variant="body2" fontWeight={600}>
                {formatCargoPrice(record)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {record.single_price != null ? "一口价" : "按吨计价"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {record.deposit ? `押金 ${record.deposit / 100} 元` : "无押金"}
              </Typography>
            </Stack>
          )}
        />
        <FunctionField
          source="userInfo"
          label="人员"
          render={(record: Cargo) => (
            <Stack spacing={1} sx={{ minWidth: 128 }}>
              <CargoUserInline source="owner" label="货主" />
              <CargoUserInline source="poster" label="发货人" />
            </Stack>
          )}
        />
      </Datagrid>
    </List>
  );
};
